import { useEffect } from 'react';
import { Outlet, useLocation, Navigate } from 'react-router-dom';
import { PanelHeader } from './PanelHeader';
import { useAuthStore } from '@/lib/stores/auth-store';
import { useSettingsStore } from '@/lib/stores/settings-store';

/**
 * Main dashboard layout — optimized for Chrome extension side panel (~400px).
 * Single-column, light mode, compact header with hamburger navigation.
 *
 * Auth guard: redirects to /login if not authenticated.
 * Waits for auth initialization before deciding (avoids flash).
 */
export function DashboardLayout(): JSX.Element {
  const location = useLocation();
  const { isAuthenticated, initialized } = useAuthStore();
  const theme = useSettingsStore((s) => s.appearance.theme);

  // Apply theme to DOM
  useEffect(() => {
    const root = document.documentElement;
    if (theme === 'dark') {
      root.classList.add('dark');
    } else if (theme === 'light') {
      root.classList.remove('dark');
    } else {
      // system
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches;
      root.classList.toggle('dark', prefersDark);
      const handler = (e: MediaQueryListEvent): void => { root.classList.toggle('dark', e.matches); };
      const mq = window.matchMedia('(prefers-color-scheme: dark)');
      mq.addEventListener('change', handler);
      return () => { mq.removeEventListener('change', handler); };
    }
  }, [theme]);

  // While auth is initializing, show nothing (prevents flash of login page)
  if (!initialized) {
    return (
      <div className="flex h-dvh items-center justify-center bg-slate-50">
        <div className="h-6 w-6 animate-spin rounded-full border-2 border-teal-200 border-t-teal-600" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return (
    <div className="flex h-dvh flex-col overflow-hidden gradient-bg">
      <PanelHeader />

      <main className="relative flex-1 overflow-y-auto px-4 py-4">
        <div key={location.pathname} className="animate-fade-in">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
